import {
  CONTENT_EDITABLE_CLASSNAME,
  SLATE_NODE_PLACEHOLDER_CLASSNAME,
  BasicType,
} from "../constants";

import { cloneDeep, get, set } from "lodash";
import React from "react";
import { Element, SectionWidgetElement } from "../typings";
import { BlockManager } from "../utils/BlockManager";
import { mergeBlock } from "../utils/mergeBlock";
import { useEmailRenderContext } from "@easy-email-pro-core/utils/EditorCore/contexts/EmailRenderContext";
import { EditorCore, NodeUtils } from "..";
import { PluginManager } from "@easy-email-pro-core/utils/plugins/PluginManager";
import {
  useWidgetElementContext,
  WidgetElementProvider,
} from "@easy-email-pro-core/utils/EditorCore/contexts/WidgetElementProvider";
import {
  useWrapperElementContext,
  WrapperElementProvider,
} from "@easy-email-pro-core/utils/EditorCore/contexts/WrapperElementProvider";
import {
  ParentElementProvider,
  useParentElementContext,
} from "@easy-email-pro-core/utils/EditorCore/contexts/ParentElementProvider";

type BlockDataItem = {
  node: Element;
  children?: React.ReactNode;
  idx?: string | null | undefined;
};

export const BlockRenderer = (props: BlockDataItem) => {
  const { children, node, idx } = props;

  const {
    mode,
    context,
    displayMode,
    keepEmptyAttributes,
    mergetagsData,
    pageVariables,
    variablesDisabled,
  } = useEmailRenderContext();
  const { element: parentWidgetElement } = useWidgetElementContext();
  const { element: wrapperElement } = useWrapperElementContext();
  const { element: parentElement } = useParentElementContext();
  const block = BlockManager.getBlockByType(node.type);
  if (!block) {
    throw new Error(`${node.type} block is not registered`);
  }
  const condition = node.logic?.condition;
  const iteration = node.logic?.iteration;

  let cloneData = {
    ...node,
    attributes: { ...node.attributes },
    mobileAttributes: { ...node.mobileAttributes },
  } as Element;

  const cssClass: string = get(cloneData, "attributes.css-class") || "";

  if (mode === "production") {
    if (cssClass) {
      set(
        cloneData,
        "attributes.css-class",
        cssClass
          .replace(CONTENT_EDITABLE_CLASSNAME, "")
          .replace(SLATE_NODE_PLACEHOLDER_CLASSNAME, "")
      );
    }
  }

  const mjClass = cloneData.attributes["mj-class"]?.trim();
  if (mjClass && context.content.data.classAttributes) {
    const classAttributes = {};
    const mjClassValues: string[] = mjClass
      .split(" ")
      .map((item) => item.trim())
      .filter(Boolean);

    Object.values(mjClassValues).forEach((key) => {
      if (get(context.content.data.classAttributes, key)) {
        Object.assign(
          classAttributes,
          get(context.content.data.classAttributes, key)
        );
      }
    });

    cloneData.attributes = mergeBlock(
      classAttributes,
      cloneData.attributes as any
    );
  }

  const matchBlockAttributes =
    context.content.data.blockAttributes?.[cloneData.type];

  if (matchBlockAttributes) {
    cloneData.attributes = mergeBlock(
      matchBlockAttributes,
      cloneData.attributes as any
    );
  }

  const matchCategoryAttributes =
    context.content.data.categoryAttributes?.[block.category];

  if (context.content.data.globalAttributes) {
    if (
      !NodeUtils.isHTMLBlockNodeElement(cloneData) &&
      !NodeUtils.isTable2TdElement(cloneData) &&
      !NodeUtils.isTable2TrElement(cloneData) &&
      !NodeUtils.isHTMLNodeElement(cloneData)
    ) {
      cloneData.attributes = mergeBlock(
        context.content.data.globalAttributes,
        cloneData.attributes as any
      );
    }
  }
  if (matchCategoryAttributes) {
    cloneData.attributes = mergeBlock(
      matchCategoryAttributes,
      cloneData.attributes as any
    );
  }

  const defaultElementAttributes = {
    ...BlockManager.getBlockByType(cloneData.type)!.defaultData.attributes,
  };

  if (defaultElementAttributes) {
    if (get(cloneData.attributes, "padding", "").trim()) {
      delete (defaultElementAttributes as any)["padding"];
      delete (defaultElementAttributes as any)["padding-top"];
      delete (defaultElementAttributes as any)["padding-bottom"];
      delete (defaultElementAttributes as any)["padding-left"];
      delete (defaultElementAttributes as any)["padding-right"];
    }
    if (get(cloneData.attributes, "inner-padding", "").trim()) {
      delete (defaultElementAttributes as any)["inner-padding"];
      delete (defaultElementAttributes as any)["inner-padding-top"];
      delete (defaultElementAttributes as any)["inner-padding-bottom"];
      delete (defaultElementAttributes as any)["inner-padding-left"];
      delete (defaultElementAttributes as any)["inner-padding-right"];
    }

    cloneData.attributes = mergeBlock(
      defaultElementAttributes,
      cloneData.attributes as any
    );
  }

  const isContainerCategory =
    NodeUtils.isSectionElement(node) || NodeUtils.isWrapperElement(node);

  if (isContainerCategory) {
    const contentBackgroundColor =
      context.content.attributes["content-background-color"];

    if (contentBackgroundColor) {
      // 如果是 section，而且它的父级是 wrapper，则不添加 background-color
      if (!wrapperElement && mode === "production") {
        cloneData.attributes = mergeBlock(
          { "background-color": contentBackgroundColor },
          cloneData.attributes as any
        );
      }
    }
  }

  if (!variablesDisabled) {
    let variables = pageVariables;
    if (NodeUtils.isWidgetElement(cloneData)) {
      variables = mergeBlock(pageVariables, cloneData.data.input);
      cloneData = EditorCore.renderWithPageVariables(
        cloneData,
        variables,
        true
      );
    } else {
      cloneData = EditorCore.renderWithPageVariables(cloneData, variables);
    }
  }

  let renderContent = PluginManager.generateResponsive({
    mode,
    children,
    context,
    idx,
    node: cloneData as Element,
    keepEmptyAttributes,
    mergetagsData,
    displayMode,
  });

  if (parentWidgetElement) {
    try {
      const mergedData = (parentWidgetElement as SectionWidgetElement).data
        .input;

      if (iteration && iteration.enabled) {
        renderContent = PluginManager.generateIterationTemplate(
          iteration,
          renderContent
        );
      }

      if (condition && condition.enabled) {
        renderContent = PluginManager.generateConditionTemplate(
          cloneDeep(condition),
          renderContent
        );
      }
      const renderMjml = EditorCore.elementComponentToMjml(renderContent, {
        pageElement: context.content,
        mode: "production",
        keepEmptyAttributes: true,
        mergetagsData: mergedData,
        displayMode: displayMode,
      });
      const finalHtml = PluginManager.renderWithData(renderMjml, mergedData);

      return <>{finalHtml}</>;
    } catch (error) {
      if (mode === "production") {
        throw error;
      }
      return <></>;
    }
  }

  if (mode === "production") {
    if (iteration && iteration.enabled) {
      renderContent = PluginManager.generateIterationTemplate(
        iteration,
        renderContent
      );
    }

    if (condition && condition.enabled) {
      renderContent = PluginManager.generateConditionTemplate(
        cloneDeep(condition),
        renderContent
      );
    }
  }

  if (NodeUtils.isWrapperElement(cloneData)) {
    renderContent = (
      <WrapperElementProvider element={cloneData}>
        {renderContent}
      </WrapperElementProvider>
    );
  }

  //  内容不可编辑的情况下，可以开启 staticLogicEnabled
  if (
    NodeUtils.isWidgetElement(cloneData) &&
    !cloneData.data.contentEditable &&
    cloneData.data.staticLogicEnabled
  ) {
    return (
      <WidgetElementProvider element={cloneData}>
        <>{renderContent}</>
      </WidgetElementProvider>
    );
  }

  if (mode === "production") {
    if (
      cloneData.id &&
      NodeUtils.isContentElement(cloneData) &&
      parentElement &&
      (NodeUtils.isPageElement(parentElement) ||
        NodeUtils.isWrapperElement(parentElement))
    ) {
      return (
        <ParentElementProvider element={null}>
          <BlockRenderer
            node={{
              type: BasicType.SECTION,
              data: {},
              attributes: {
                padding: "0px 0px 0px 0px",
              },
              children: [],
            }}
          >
            <BlockRenderer
              node={{
                type: BasicType.COLUMN,
                data: {},
                attributes: {},
                children: [],
              }}
            >
              {renderContent}
            </BlockRenderer>
          </BlockRenderer>
        </ParentElementProvider>
      );
    }
  }

  // 如果元素没有id，则不渲染ParentElement, 因为只需要JSON的block，JSON 中的 block 都有 id
  if (!cloneData.id) {
    return <>{renderContent}</>;
  }
  return (
    <ParentElementProvider element={cloneData}>
      {renderContent}
    </ParentElementProvider>
  );
};
